import type { PropsWithChildren } from 'react';

import { Playground } from '../../../components/playground/Playground';
import { PlaygroundClasses } from '../../../components/playground/PlaygroundClasses';
import { Description } from '../../../components/Description';
import { Headline } from '../../../components/Headline';

export const Rounded = () => (
    <div>
        <Headline anchor='rounded'>Rounded</Headline>
        <Playground example={roundedExample} classes={roundedClasses} />
        <Headline anchor='rounded-size' headlineType='h4'>
            Rounded sizes
        </Headline>
        <Description>
            <p>
                Choose a radius that matches the size and visual emphasis of the element. Use <code>rounded-small</code>{' '}
                for compact elements and dense layouts, and <code>rounded</code> for most containers. Use{' '}
                <code>rounded-large</code> for larger cards and panels, and <code>rounded-extra-large</code> for
                prominent surfaces such as feature containers or banners. Keep the same radius for equivalent elements
                to create a consistent visual hierarchy.
            </p>
        </Description>
        <Playground example={roundedSizeExample} classes={roundedSizesClasses} />
        <Headline anchor='rounded-inner' headlineType='h4'>
            Rounded inner blocks
        </Headline>
        <Description>
            <p>
                Use <code>rounded-inner</code> for nested blocks inside a rounded container. It applies a radius one
                step smaller than the parent&apos;s radius, keeping parallel edges visually aligned. This is useful when
                both layers remain visible, for example in cards, bordered panels, or containers with contrasting
                backgrounds and padding.
            </p>
        </Description>
        <Playground example={roundedInnerExample} classes={roundedInnerClasses} />
    </div>
);

type BoxProps = {
    size: string;
    ratio: string;
    className: string;
};

const Box = ({ size, ratio, className, children }: PropsWithChildren<BoxProps>) => (
    <div
        className={`
            width-${size} aspect-ratio-${ratio} bg-highlight
            display-flex align-items-center justify-content-center ${className}`}
    >
        {children}
    </div>
);

const roundedExample = (
    <div>
        <div className='display-flex flex-wrap gap-20 margin-bottom-20'>
            <Box size='100' ratio='1' className='rounded'>
                rounded
            </Box>
            <Box size='100' ratio='1' className='rounded-circle'>
                ...-circle
            </Box>
        </div>
        <div className='display-flex flex-wrap gap-20 margin-bottom-20'>
            <Box size='150' ratio='2-1' className='rounded-top'>
                ...-top
            </Box>
            <Box size='150' ratio='2-1' className='rounded-bottom'>
                ...-bottom
            </Box>
            <Box size='150' ratio='2-1' className='rounded-left'>
                ...-left
            </Box>
            <Box size='150' ratio='2-1' className='rounded-right'>
                ...-right
            </Box>
        </div>
        <div className='display-flex flex-wrap gap-20'>
            <Box size='150' ratio='2-1' className='rounded-top-left'>
                ...-top-left
            </Box>
            <Box size='150' ratio='2-1' className='rounded-top-right'>
                ...-top-right
            </Box>
            <Box size='150' ratio='2-1' className='rounded-bottom-right'>
                ...-bottom-right
            </Box>
            <Box size='150' ratio='2-1' className='rounded-bottom-left'>
                ...-bottom-left
            </Box>
        </div>
    </div>
);

const roundedClasses = (
    <>
        <PlaygroundClasses label='None' classes='rounded-none' />
        <PlaygroundClasses label='Inherit' classes='rounded-inherit' />
        <PlaygroundClasses label='Surrounding' classes={['rounded', 'rounded-circle']} />
        <PlaygroundClasses label='Top & Bottom' prefix='rounded-' classes={['top', 'bottom']} />
        <PlaygroundClasses label='Left & Right' prefix='rounded-' classes={['left', 'right']} />
        <PlaygroundClasses
            label='Single corner'
            prefix='rounded-'
            classes={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
        />
        <PlaygroundClasses label='None top & bottom' prefix='rounded-' classes={['top-none', 'bottom-none']} />
        <PlaygroundClasses label='None left & right' prefix='rounded-' classes={['left-none', 'right-none']} />
        <PlaygroundClasses
            label='None single corner'
            prefix='rounded-'
            classes={['top-left-none', 'top-right-none', 'bottom-left-none', 'bottom-right-none']}
        />
    </>
);

const roundedSizeExample = (
    <div className='display-flex flex-wrap gap-20'>
        <Box size='150' ratio='2-1' className='rounded-small'>
            ...-small
        </Box>
        <Box size='150' ratio='2-1' className='rounded'>
            ...
        </Box>
        <Box size='150' ratio='2-1' className='rounded-large'>
            ...-large
        </Box>
        <Box size='150' ratio='2-1' className='rounded-extra-large'>
            ...-extra-large
        </Box>
    </div>
);

const roundedSizesClasses = (
    <>
        <PlaygroundClasses
            label='Small'
            classes={[
                'rounded-small',
                'rounded-top-small',
                'rounded-bottom-small',
                'rounded-left-small',
                'rounded-right-small',
                'rounded-top-left-small',
                'rounded-top-right-small',
                'rounded-bottom-left-small',
                'rounded-bottom-right-small',
            ]}
        />
        <PlaygroundClasses
            label='Default'
            classes={[
                'rounded',
                'rounded-top',
                'rounded-bottom',
                'rounded-left',
                'rounded-right',
                'rounded-top-left',
                'rounded-top-right',
                'rounded-bottom-left',
                'rounded-bottom-right',
            ]}
        />
        <PlaygroundClasses
            label='Large'
            classes={[
                'rounded-large',
                'rounded-top-large',
                'rounded-bottom-large',
                'rounded-left-large',
                'rounded-right-large',
                'rounded-top-left-large',
                'rounded-top-right-large',
                'rounded-bottom-left-large',
                'rounded-bottom-right-large',
            ]}
        />
        <PlaygroundClasses
            label='Extra large'
            classes={[
                'rounded-extra-large',
                'rounded-top-extra-large',
                'rounded-bottom-extra-large',
                'rounded-left-extra-large',
                'rounded-right-extra-large',
                'rounded-top-left-extra-large',
                'rounded-top-right-extra-large',
                'rounded-bottom-left-extra-large',
                'rounded-bottom-right-extra-large',
            ]}
        />
    </>
);

const roundedInnerExample = (
    <div className='display-flex flex-wrap gap-20'>
        <Box size='150' ratio='2-1' className='rounded padding-1'>
            <div className='rounded-inner bg-white padding-15 width-100pct height-100pct display-grid place-items-center'>
                ...-inner
            </div>
        </Box>
        <Box size='150' ratio='2-1' className='rounded-large padding-1'>
            <div className='rounded-inner bg-white padding-15 width-100pct height-100pct display-grid place-items-center'>
                ...-inner
            </div>
        </Box>
        <Box size='150' ratio='2-1' className='rounded-top-left-extra-large padding-1'>
            <div className='rounded-inner bg-white padding-15 width-100pct height-100pct display-grid place-items-center'>
                ...-inner
            </div>
        </Box>
    </div>
);

const roundedInnerClasses = <PlaygroundClasses label='Inner' classes='rounded-inner' />;
